import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import CategorySuggestionModal from './CategorySuggestionModal'
import { invalidateSavedListsCache } from './savedListsCache'
import StoreLayoutSchematic from './components/StoreLayoutSchematic'
import { authenticatedFetch, getAuthHeaders } from './utils/auth.js'
import { useAuth } from './contexts/AuthContext'
import { groceryLineCoveredByPantry } from './utils/pantryMatch.js'
import { capitalizeFirstGroceryWord } from './utils/groceryItemDisplayName.js'
import { groceryItemAlphaSortKey, groceryLookupKey, ingredientDisplayQty } from './utils/ingredientQuantity.js'
import { formatShoppingListPlainText } from './utils/formatShoppingListPlainText.js'
import {
  applyLookupMeta,
  indexLookupResultsByQuery,
} from './utils/enrichGroceryItemsWithLookup.js'
import {
  displayGroceryCategoryFromKey,
  groceryCategoryGroupingKey,
} from './utils/groceryCategoryNormalize.js'
import { LIST_ACTIONS } from './utils/shoppingListActions.js'
import { buildShoppingListSignature } from './utils/shoppingListState.js'
import {
  detachRecipeFromShoppingList,
  recipeRemovalConfirmCopy,
  recipeSourcesFromItem,
} from './utils/recipeShoppingListSources.js'
import { Button } from './design-system/primitives/index.js'
import {
  ShopV2StatChips,
  ShopV2SortPills,
  ShopV2Sidebar,
  ShopV2MobileBottomBar,
} from './components/shop/ShopV2Chrome.jsx'

const SHOP_V2_BREAKPOINT = 768
const GROCERY_SWIPE_REMOVE_WIDTH = 80

function groceryListReplaceSignature(listId, arr) {
  return `${listId ?? 'none'}:${buildShoppingListSignature(arr)}`
}

/**
 * Maps store names to their corresponding layout HTML file paths.
 * @param {string} storeName - The name of the store
 * @returns {string|null} - The path to the layout file, or null if not found
 */
function getStoreLayoutUrl(storeName) {
  if (!storeName) return null
  
  const normalizedName = storeName.toLowerCase().trim()

  // Legacy DB label pointed at the Shaws Peterborough layout, not Hannaford Keene.
  if (
    normalizedName.includes('keene nh store') &&
    normalizedName.includes('updated')
  ) {
    return '/shaws_store_layout_schematic.html'
  }
  
  // Map store names to layout files
  // Check for specific locations first (more specific matches)
  if (normalizedName.includes("keene")) {
    return '/hannafords_keene_store_layout_schematic.html'
  }
  
  if (normalizedName.includes("shaw")) {
    return '/shaws_store_layout_schematic.html'
  }
  
  // General Hannaford stores (check after specific locations)
  if (normalizedName.includes("hannaford")) {
    return '/hannafords_store_layout_schematic.html'
  }
  
  // Add more store mappings here as needed
  // if (normalizedName.includes("stop")) {
  //   return '/stop_and_shop_layout.html'
  // }
  
  return null
}

function GroceryList({
  items = [],
  onUpdateItems,
  onDispatchListAction,
  onClearAll,
  onSyncCanonicalCart,
  loadedSavedListId,
  loadedSavedListName = null,
  loadedSavedListIsRecipe = false,
  loadedSavedListOriginalItemNames = null,
  selectedStore = null,
  pantryItemNames = null,
  storeHeaderSlot = null,
  pageNotices = null,
}) {
  const { isAuthenticated } = useAuth()
  const [localItems, setLocalItems] = useState([])
  const [sortBy, setSortBy] = useState('recipe') // 'recipe' | 'category' | 'list' | 'aisle'
  const [showStoreLayoutModal, setShowStoreLayoutModal] = useState(false)
  const [storeLayoutContent, setStoreLayoutContent] = useState(null)
  const [loadingLayout, setLoadingLayout] = useState(false)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [recipeRemoval, setRecipeRemoval] = useState(null)
  const [textListCopied, setTextListCopied] = useState(false)
  const [isRefreshingMappings, setIsRefreshingMappings] = useState(false)
  const [isShopMobile, setIsShopMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= SHOP_V2_BREAKPOINT,
  )
  const persistTimerRef = useRef(null)
  const textListCopiedTimerRef = useRef(null)
  const localItemsRef = useRef([])
  const lastParentNotifyJsonRef = useRef(null)
  const skipNextParentNotifyRef = useRef(false)
  /** Ids removed via X that parent/cart may still briefly hold — do not re-adopt them. */
  const pendingRemovedIdsRef = useRef(new Set())
  const itemsFromParentRef = useRef(items)
  itemsFromParentRef.current = items

  const itemsPropSyncKey = useMemo(
    () => groceryListReplaceSignature(loadedSavedListId, items),
    [items, loadedSavedListId],
  )

  useEffect(() => {
    localItemsRef.current = localItems
  }, [localItems])

  useEffect(() => {
    return () => {
      if (textListCopiedTimerRef.current) {
        clearTimeout(textListCopiedTimerRef.current)
      }
    }
  }, [])

  const copyTextList = useCallback(async () => {
    const text = formatShoppingListPlainText(localItemsRef.current)
    if (!text) return
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        throw new Error('clipboard unavailable')
      }
    } catch {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
      } finally {
        document.body.removeChild(ta)
      }
    }
    setTextListCopied(true)
    if (textListCopiedTimerRef.current) {
      clearTimeout(textListCopiedTimerRef.current)
    }
    textListCopiedTimerRef.current = setTimeout(() => {
      setTextListCopied(false)
      textListCopiedTimerRef.current = null
    }, 2000)
  }, [])

  // Initialize local items from props only when content actually changed.
  // Using a functional update and value comparison avoids an infinite loop when the parent
  // passes a new array reference on every render (e.g. after category refresh in GrocerySearch).
  // Compare by item identity (id) so that toggling "found" doesn't get reverted when the parent's
  // items are still in the old order. If local state has completion toggles not yet in props, keep local.
  useLayoutEffect(() => {
    const rawNextItems = itemsFromParentRef.current
    const prevItems = localItemsRef.current
    const pendingRemoved = pendingRemovedIdsRef.current

    // Drop ids the user already removed; clear pending once parent no longer has them.
    if (pendingRemoved.size > 0) {
      for (const id of [...pendingRemoved]) {
        const stillInParent = rawNextItems.some((it) => String(it?.id) === String(id))
        if (!stillInParent) pendingRemoved.delete(id)
      }
    }
    const nextItems =
      pendingRemoved.size > 0
        ? rawNextItems.filter((it) => !pendingRemoved.has(String(it?.id)))
        : rawNextItems

    // React can re-run layout effects after state updates in the same commit cycle.
    // Avoid scheduling any setState unless we truly need to adopt parent items.
    if (nextItems.length === 0 && prevItems.length === 0) return
    const sigPrev = groceryListReplaceSignature(loadedSavedListId, prevItems)
    const sigNext = groceryListReplaceSignature(loadedSavedListId, nextItems)
    if (sigPrev === sigNext) return

    const itemsById = new Map(nextItems.map(it => [it.id, it]))
    const prevById = new Map(prevItems.map(p => [p.id, p]))
    // If any local item is completed but props say not, keep local (avoid reverting a recent toggle)
    for (const [id, p] of prevById) {
      const it = itemsById.get(id)
      if (it && p.completed && !it.completed) return
    }

    skipNextParentNotifyRef.current = true
    setLocalItems(nextItems)
  }, [itemsPropSyncKey, loadedSavedListId])

  // Save to localStorage and notify parent when local items change (from user actions or real prop updates).
  useEffect(() => {
    try {
      if (localItems.length > 0) {
        localStorage.setItem('grocery_list', JSON.stringify(localItems))
      } else {
        localStorage.removeItem('grocery_list')
      }
    } catch {
      // Ignore storage errors.
    }
    if (skipNextParentNotifyRef.current) {
      skipNextParentNotifyRef.current = false
      let ser = ''
      try {
        ser = JSON.stringify(localItems)
      } catch {
        ser = ''
      }
      lastParentNotifyJsonRef.current = ser || null
      return
    }
    // Parent already matches this list (same signature as shopping list reducer); do not echo REPLACE.
    if (groceryListReplaceSignature(loadedSavedListId, localItems) === itemsPropSyncKey) {
      let ser = ''
      try {
        ser = JSON.stringify(localItems)
      } catch {
        ser = ''
      }
      lastParentNotifyJsonRef.current = ser || null
      return
    }

    // Avoid spamming parent updates with identical payloads (prevents Shop flicker and loops).
    let serialized = ''
    try {
      serialized = JSON.stringify(localItems)
    } catch {
      serialized = ''
    }
    if (serialized && lastParentNotifyJsonRef.current === serialized) return
    lastParentNotifyJsonRef.current = serialized || null

    if (onDispatchListAction) {
      onDispatchListAction({
        type: LIST_ACTIONS.REPLACE_FROM_LIST,
        items: localItems,
        listId: loadedSavedListId ?? null,
        listName: loadedSavedListName ?? null,
        isRecipe: loadedSavedListIsRecipe,
        preserveCurrentOnSameList: false,
      })
    } else if (onUpdateItems) {
      onUpdateItems(localItems)
    }
  }, [
    itemsPropSyncKey,
    loadedSavedListId,
    loadedSavedListIsRecipe,
    loadedSavedListName,
    localItems,
    onDispatchListAction,
    onUpdateItems,
  ])

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const onResize = () => setIsShopMobile(window.innerWidth <= SHOP_V2_BREAKPOINT)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Load from localStorage on component mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('grocery_list')
      if (saved) {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Only load from localStorage if no items are passed from parent
          if (items.length === 0) {
            const withDisplay = parsed.map((row) =>
              row && typeof row === 'object'
                ? { ...row, item: capitalizeFirstGroceryWord(row.item) }
                : row
            )
            setLocalItems(withDisplay)
          }
        }
      }
    } catch (e) {
      console.error('Failed to load grocery list from localStorage:', e)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for logout event and reset state
  useEffect(() => {
    function handleLogout() {
      setLocalItems([])
      setShowStoreLayoutModal(false)
      setShowClearConfirm(false)
      setRecipeRemoval(null)
    }
    window.addEventListener('userLoggedOut', handleLogout)
    return () => window.removeEventListener('userLoggedOut', handleLogout)
  }, [])

  // Load store layout content when modal opens or store changes
  useEffect(() => {
    async function loadStoreLayout() {
      if (!showStoreLayoutModal || !selectedStore) {
        // Clear layout content if modal is closed or no store selected
        if (!showStoreLayoutModal || !selectedStore) {
          setStoreLayoutContent(null)
        }
        return
      }
      
      const layoutUrl = getStoreLayoutUrl(selectedStore.name)
      if (!layoutUrl) {
        setStoreLayoutContent(null)
        return
      }
      
      setLoadingLayout(true)
      try {
        const response = await fetch(layoutUrl)
        if (!response.ok) {
          throw new Error(`Failed to load store layout: ${response.status}`)
        }
        const htmlContent = await response.text()
        setStoreLayoutContent(htmlContent)
        console.log(`Store layout loaded for: ${selectedStore.name}`)
      } catch (error) {
        console.error('Error loading store layout:', error)
        setStoreLayoutContent(null)
      } finally {
        setLoadingLayout(false)
      }
    }
    
    loadStoreLayout()
  }, [showStoreLayoutModal, selectedStore])

  // Make categories collapsible on mobile after content loads
  useEffect(() => {
    if (!storeLayoutContent || !showStoreLayoutModal) return

    // Function to check if device is mobile
    const isMobile = () => {
      return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    }

    // Function to setup collapsible categories
    const setupCollapsibleCategories = () => {
      if (!isMobile()) return // Only on mobile

      const categorySelectors = [
        '.bakery-categories',
        '.produce-categories',
        '.aisle-categories',
        '.fresh-meat-categories',
        '.fresh-fish-categories',
        '.dairy-categories'
      ]

      categorySelectors.forEach(selector => {
        const categoryLists = document.querySelectorAll(selector)
        categoryLists.forEach(list => {
          // Skip if already processed
          if (list.dataset.collapsibleSetup === 'true') return
          list.dataset.collapsibleSetup = 'true'

          // Find the parent section/zone/aisle
          const parent = list.closest('.bakery-zone, .produce-zone, .aisle, .fresh-meat-section, .fresh-fish-section, .dairy-section')
          if (!parent) return

          // Find the header (h2)
          const header = parent.querySelector('h2')
          if (!header) return

          // Hide categories by default on mobile
          list.style.display = 'none'
          list.style.transition = 'max-height 0.3s ease, opacity 0.3s ease'
          list.style.maxHeight = '0'
          list.style.opacity = '0'
          list.style.overflow = 'hidden'

          // Make header clickable
          header.style.cursor = 'pointer'
          header.style.userSelect = 'none'
          header.style.position = 'relative'
          header.style.paddingRight = '30px'

          // Add chevron icon
          let chevron = header.querySelector('.collapse-chevron')
          if (!chevron) {
            chevron = document.createElement('span')
            chevron.className = 'collapse-chevron'
            chevron.innerHTML = '▼'
            chevron.style.position = 'absolute'
            chevron.style.right = '0'
            chevron.style.top = '50%'
            chevron.style.transform = 'translateY(-50%) rotate(-90deg)'
            chevron.style.transition = 'transform 0.3s ease'
            chevron.style.fontSize = '0.8em'
            header.appendChild(chevron)
          }

          // Toggle function
          const toggleCategories = () => {
            const isExpanded = list.style.display !== 'none'
            
            if (isExpanded) {
              // Collapse
              list.style.maxHeight = '0'
              list.style.opacity = '0'
              setTimeout(() => {
                list.style.display = 'none'
              }, 300)
              if (chevron) {
                chevron.style.transform = 'translateY(-50%) rotate(-90deg)'
              }
            } else {
              // Expand
              list.style.display = 'block'
              // Force reflow
              list.offsetHeight
              const scrollHeight = list.scrollHeight
              list.style.maxHeight = scrollHeight + 'px'
              list.style.opacity = '1'
              if (chevron) {
                chevron.style.transform = 'translateY(-50%) rotate(0deg)'
              }
            }
          }

          // Add click handler
          header.addEventListener('click', toggleCategories)
        })
      })
    }

    // Setup after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      setupCollapsibleCategories()
    }, 100)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [storeLayoutContent, showStoreLayoutModal])

  // Handle ESC key to close store layout modal and the meal-removal confirm.
  useEffect(() => {
    function handleEscapeKey(event) {
      if (event.key !== 'Escape') return
      if (showStoreLayoutModal) setShowStoreLayoutModal(false)
      if (recipeRemoval) setRecipeRemoval(null)
    }

    if (showStoreLayoutModal || recipeRemoval) {
      document.addEventListener('keydown', handleEscapeKey)
    }
    if (showStoreLayoutModal) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
      document.body.style.overflow = ''
    }
  }, [showStoreLayoutModal, recipeRemoval])

  function toggleItemComplete(itemId) {
    const updatedItems = localItems.map(item =>
      item.id === itemId
        ? { ...item, completed: !item.completed, completedAt: !item.completed ? Date.now() : null }
        : item
    )

    // Sort items: incomplete first, then completed (by completion time)
    const sortedItems = updatedItems.sort((a, b) => {
      if (a.completed && !b.completed) return 1
      if (!a.completed && b.completed) return -1
      if (a.completed && b.completed) {
        return (b.completedAt || 0) - (a.completedAt || 0) // Most recently completed first
      }
      return 0
    })

    setLocalItems(sortedItems)
    schedulePersistLoadedList()
  }

  function openRecipeRemoval(recipeGroup) {
    const groupKey = String(recipeGroup?.groupKey || '')
    if (!groupKey || groupKey === '__other__') return
    const recipeId = groupKey.startsWith('id:') ? groupKey.slice(3) : ''
    const recipeName = String(recipeGroup?.recipe || '').trim()
    const preview = detachRecipeFromShoppingList(localItemsRef.current, { recipeId, recipeName })
    const copy = recipeRemovalConfirmCopy(recipeName, preview.removedItems.length, preview.keptSharedItems)
    setRecipeRemoval({ recipeId, recipeName, ...copy })
  }

  function confirmRecipeRemoval() {
    const pending = recipeRemoval
    if (!pending) return
    const { items: next, removedItems } = detachRecipeFromShoppingList(localItemsRef.current, {
      recipeId: pending.recipeId,
      recipeName: pending.recipeName,
    })
    for (const item of removedItems) {
      if (item?.id != null) pendingRemovedIdsRef.current.add(String(item.id))
    }
    setRecipeRemoval(null)
    setLocalItems(next)
    schedulePersistLoadedList()
    if (typeof onSyncCanonicalCart === 'function') {
      onSyncCanonicalCart(next)
    }
  }

  function removeItem(itemId) {
    if (itemId != null) pendingRemovedIdsRef.current.add(String(itemId))
    const next = localItems.filter(item => item.id !== itemId)
    setLocalItems(next)
    schedulePersistLoadedList()
    if (typeof onSyncCanonicalCart === 'function') {
      onSyncCanonicalCart(next)
    }
  }

  function clearAll() {
    setShowClearConfirm(false)
    pendingRemovedIdsRef.current.clear()
    // Cancel any pending auto-save so it cannot re-PUT stale items after clear.
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current)
      persistTimerRef.current = null
    }
    setLocalItems([])
    try {
      localStorage.removeItem('grocery_list')
    } catch {
      // ignore
    }
    if (onClearAll) {
      onClearAll()
    } else if (onDispatchListAction) {
      onDispatchListAction({ type: LIST_ACTIONS.CLEAR_ALL })
    }
  }

  /** Silent auto-persist of the live shopping list (no named Save UI). */
  const performSaveChangesToLoadedList = useCallback(
    async ({ listId: listIdOverride, items: itemsOverride } = {}) => {
      const listId = listIdOverride || loadedSavedListId
      if (!listId) return
      try {
        const sourceItems = Array.isArray(itemsOverride) ? itemsOverride : localItems
        const itemsToSave = sourceItems.map((item) => ({
          ...item,
          addedAt: item.addedAt || Date.now(),
          completedAt: item.completedAt || null,
        }))
        if (loadedSavedListIsRecipe && itemsToSave.length === 0) {
          return
        }
        const response = await authenticatedFetch(`/saved-lists/${listId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            items: itemsToSave,
          }),
        })
        if (response.status === 401 || response.status === 403) {
          console.warn('[GroceryList] Authentication error during update')
          return
        }
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'Failed to save changes')
        }
        invalidateSavedListsCache()
      } catch (error) {
        console.error('Error saving changes:', error)
      }
    },
    [loadedSavedListId, localItems, loadedSavedListIsRecipe]
  )

  // When the list is exactly a single loaded recipe, skip auto-persist until the user edits ingredients.
  function normalizeItemName(str) {
    return (str || '').trim().toLowerCase()
  }

  const lineMatchesPantry = useMemo(() => {
    if (!pantryItemNames || pantryItemNames.length === 0) {
      return () => false
    }
    const names = pantryItemNames
    return (line) => groceryLineCoveredByPantry(line, names)
  }, [pantryItemNames])

  const listIsOnlySingleRecipe =
    !!(
      loadedSavedListId &&
      loadedSavedListIsRecipe &&
      Array.isArray(loadedSavedListOriginalItemNames) &&
      loadedSavedListOriginalItemNames.length > 0 &&
      localItems.length === loadedSavedListOriginalItemNames.length
    ) &&
    (() => {
      const a = localItems.map((i) => normalizeItemName(i.item)).sort()
      const b = [...loadedSavedListOriginalItemNames].sort()
      return a.length === b.length && a.every((n, i) => n === b[i])
    })()

  const schedulePersistLoadedList = useCallback(() => {
    if (!isAuthenticated || !loadedSavedListId) return
    if (loadedSavedListIsRecipe && listIsOnlySingleRecipe) return

    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current)
      persistTimerRef.current = null
    }

    const listId = loadedSavedListId
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null
      const latest = localItemsRef.current || []
      void performSaveChangesToLoadedList({ listId, items: latest })
    }, 450)
  }, [
    isAuthenticated,
    loadedSavedListId,
    loadedSavedListIsRecipe,
    listIsOnlySingleRecipe,
    performSaveChangesToLoadedList,
  ])

  function sortItemsByAisle(items) {
    // Group items by aisle
    const aisleGroups = {}
    
    items.forEach(item => {
      const aisleKey = item.aisle_id || 'no-aisle'
      const aisleName = item.aisle_name || 'Unknown Aisle'
      
      if (!aisleGroups[aisleKey]) {
        aisleGroups[aisleKey] = {
          aisle_id: item.aisle_id,
          aisle_name: aisleName,
          items: []
        }
      }
      aisleGroups[aisleKey].items.push(item)
    })
    
    // Sort aisles: non-numeric aisles first (alphabetically), then numeric aisles (numerically)
    const sortedAisles = Object.values(aisleGroups).sort((a, b) => {
      const aIsNumeric = /^\d+$/.test(a.aisle_id)
      const bIsNumeric = /^\d+$/.test(b.aisle_id)
      
      // Non-numeric aisles come first
      if (!aIsNumeric && bIsNumeric) return -1
      if (aIsNumeric && !bIsNumeric) return 1
      
      // Both non-numeric: sort alphabetically by name
      if (!aIsNumeric && !bIsNumeric) {
        return a.aisle_name.localeCompare(b.aisle_name)
      }
      
      // Both numeric: sort numerically
      return parseInt(a.aisle_id) - parseInt(b.aisle_id)
    })
    
    return sortedAisles
  }

  /** Re-fetch aisle mappings for current list and store; updates localItems and notifies parent. Manual only to avoid flicker. */
  async function refreshStoreLayoutMappings() {
    if (!selectedStore?.id || localItems.length === 0) return
    setIsRefreshingMappings(true)
    try {
      const seen = new Set()
      const itemNames = localItems
        .map((it) => groceryLookupKey(it.item) || String(it.item || '').trim().toLowerCase())
        .filter(Boolean)
        .filter((name) => {
          if (seen.has(name)) return false
          seen.add(name)
          return true
        })
      if (itemNames.length === 0) {
        setIsRefreshingMappings(false)
        return
      }
      const params = new URLSearchParams()
      params.set('store', selectedStore.id)
      const response = await fetch(`/grocery/lookup/batch?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ items: itemNames })
      })
      if (!response.ok) {
        setIsRefreshingMappings(false)
        return
      }
      const data = await response.json()
      const lookupByKey = indexLookupResultsByQuery(itemNames, data.results)
      const updated = localItems.map((item) => {
        const exact = (item.item || '').toLowerCase()
        const stripped = groceryLookupKey(item.item || '')
        const r = lookupByKey.get(stripped) || lookupByKey.get(exact)
        return applyLookupMeta(item, r)
      })
      setLocalItems(updated)
    } catch (err) {
      console.error('Error refreshing store layout mappings:', err)
    } finally {
      setIsRefreshingMappings(false)
    }
  }
  
  function sortItemsByCategory(items) {
    // Group by normalized category (trim, unicode spaces, case) so "Baking",
    // "baking", and "Baking\u00a0" collapse to one heading.
    const categoryGroups = {}
    
    items.forEach(item => {
      const gkey = groceryCategoryGroupingKey(item.category)
      const bucket = gkey || '__uncategorized__'
      const categoryName = gkey
        ? displayGroceryCategoryFromKey(gkey)
        : 'Uncategorized'

      if (!categoryGroups[bucket]) {
        categoryGroups[bucket] = {
          category: categoryName,
          groupKey: bucket,
          items: [],
        }
      }
      categoryGroups[bucket].items.push(item)
    })
    
    // Sort categories alphabetically
    const sortedCategories = Object.values(categoryGroups).sort((a, b) => {
      // Put "Uncategorized" at the end
      if (a.groupKey === '__uncategorized__' && b.groupKey !== '__uncategorized__') return 1
      if (a.groupKey !== '__uncategorized__' && b.groupKey === '__uncategorized__') return -1
      return a.category.localeCompare(b.category)
    })
    
    return sortedCategories
  }

  /** Group by recipe provenance stamped when recipes / meal-plan groceries were added. */
  function sortItemsByRecipe(items) {
    const recipeGroups = {}
    const ensureGroup = (groupKey, label) => {
      if (!recipeGroups[groupKey]) {
        recipeGroups[groupKey] = {
          groupKey,
          recipe: label,
          items: [],
        }
      }
      return recipeGroups[groupKey]
    }

    items.forEach((item) => {
      const sources = recipeSourcesFromItem(item)
      if (!sources.length) {
        ensureGroup('__other__', 'Other items').items.push(item)
        return
      }
      sources.forEach((source) => {
        const groupKey = source.id
          ? `id:${source.id}`
          : `name:${source.name.toLowerCase()}`
        // Same physical line may appear under multiple recipes when shared.
        ensureGroup(groupKey, source.name).items.push(item)
      })
    })
    return Object.values(recipeGroups).sort((a, b) => {
      if (a.groupKey === '__other__' && b.groupKey !== '__other__') return 1
      if (a.groupKey !== '__other__' && b.groupKey === '__other__') return -1
      return a.recipe.localeCompare(b.recipe, undefined, { sensitivity: 'base' })
    })
  }

  /** Incomplete first, then found (most recently completed first within found). */
  function sortItemsFoundOrder(items) {
    return [...items].sort((a, b) => {
      if (a.completed && !b.completed) return 1
      if (!a.completed && b.completed) return -1
      if (a.completed && b.completed) {
        return (b.completedAt || 0) - (a.completedAt || 0)
      }
      return 0
    })
  }

  const aisleGroupsView = useMemo(
    () =>
      sortItemsByAisle(localItems).map((g) => ({
        ...g,
        items: sortItemsFoundOrder(g.items),
      })),
    [localItems]
  )

  const categoryGroupsView = useMemo(
    () =>
      sortItemsByCategory(localItems).map((g) => ({
        ...g,
        items: sortItemsFoundOrder(g.items),
      })),
    [localItems]
  )

  const recipeGroupsView = useMemo(
    () =>
      sortItemsByRecipe(localItems).map((g) => ({
        ...g,
        items: sortItemsFoundOrder(g.items),
      })),
    // sortItemsByRecipe / sortItemsFoundOrder are defined in-render; localItems is the only input.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match aisle/category group memos
    [localItems]
  )

  const alphaSortedItems = useMemo(() => {
    return [...localItems].sort((a, b) => {
      const byName = groceryItemAlphaSortKey(a.item).localeCompare(
        groceryItemAlphaSortKey(b.item),
        undefined,
        { sensitivity: 'base' },
      )
      if (byName !== 0) return byName
      return String(a.item || '').localeCompare(String(b.item || ''), undefined, {
        sensitivity: 'base',
      })
    })
  }, [localItems])

  const shopCheckedCount = useMemo(
    () => localItems.filter((item) => item.completed).length,
    [localItems],
  )
  const shopTotalCount = localItems.length
  const shopAllDone = shopTotalCount > 0 && shopCheckedCount === shopTotalCount
  const shopAisleCount = aisleGroupsView.length
  const shopMealCount = useMemo(
    () =>
      new Set(
        localItems
          .map((item) => item?.source_recipe_id || item?.source_recipe_name)
          .filter(Boolean)
          .map(String),
      ).size,
    [localItems],
  )

  return (
    <div className={`shop-grocery-list-inner shop-v2${localItems.length === 0 ? ' shop-v2--empty' : ''}`}>
      <div className="shop-v2-header">
        <div className="shop-v2-header-top">
          <div className="shop-v2-header-copy">
            <header className="shop-v2-page-header" aria-label="Shop page title">
              <div className="page-intro-eyebrow-slot">
                <p className="eyebrow planner-eyebrow-tight">Your grocery run</p>
              </div>
              <h1 className="h-display shop-page-title">Shop</h1>
            </header>
          </div>
          <div className="shop-list-heading-right">{storeHeaderSlot}</div>
        </div>
        {pageNotices}
        <ShopV2StatChips
          total={shopTotalCount}
          checked={shopCheckedCount}
          aisleCount={shopAisleCount}
          mealCount={shopMealCount}
          showChecked={!isShopMobile}
        />
      </div>

      <div className="shop-v2-body">
        <div className="shop-v2-main">
          {localItems.length > 0 ? (
            <div className="shop-v2-list-toolbar">
              <ShopV2SortPills sortBy={sortBy} onSortChange={setSortBy} />
            </div>
          ) : null}
          {isShopMobile && localItems.length > 0 ? (
            <p className="shop-swipe-hint">Swipe left on an item to remove</p>
          ) : null}

          {localItems.length === 0 ? (
            <div className="shop-empty-state" role="status">
              <i className="fas fa-shopping-cart shop-empty-icon" aria-hidden="true" />
              <p className="shop-empty-title">Your grocery list is empty</p>
              <p className="shop-empty-sub">Add missing ingredients from a meal, or type them below</p>
            </div>
          ) : (
            <div>
              {sortBy === 'aisle' ? (
                <div className="shop-list-groups">
                  {aisleGroupsView.map((aisle) => (
                    <div key={aisle.aisle_id || 'no-aisle'} className="shop-list-group">
                      <div className="shop-list-group-header">
                        <span className={`shop-list-group-label${isShopMobile ? ' shop-list-group-label--sentence' : ''}`}>
                          <i className="fas fa-store shop-list-group-icon" aria-hidden="true" />
                          <span className="shop-list-group-label-text">{aisle.aisle_name}</span>
                        </span>
                        <span className="shop-list-group-count">
                          {aisle.items.length} {aisle.items.length === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                      <div className="shop-list-group-items">
                        {aisle.items.map((item) => (
                          <GroceryListItem
                            key={item.id}
                            item={item}
                            onToggleComplete={toggleItemComplete}
                            onRemove={removeItem}
                            inPantry={lineMatchesPantry(item.item)}
                            isMobile={isShopMobile}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : sortBy === 'recipe' ? (
                <div className="shop-list-groups">
                  {recipeGroupsView.map((recipeGroup) => (
                    <div key={recipeGroup.groupKey} className="shop-list-group">
                      <div className="shop-list-group-header shop-list-group-header--recipe">
                        <span className="shop-list-group-label shop-list-group-label--sentence">
                          <i className="fas fa-utensils shop-list-group-icon" aria-hidden="true" />
                          <span className="shop-list-group-label-text">{recipeGroup.recipe}</span>
                        </span>
                        <span className="shop-list-group-actions">
                          <span className="shop-list-group-count">
                            {recipeGroup.items.length}{' '}
                            {recipeGroup.items.length === 1 ? 'item' : 'items'}
                          </span>
                          {recipeGroup.groupKey !== '__other__' ? (
                            <button
                              type="button"
                              className="shop-list-group-remove"
                              aria-label={`Take ${recipeGroup.recipe} off the list`}
                              onClick={() => openRecipeRemoval(recipeGroup)}
                            >
                              Take off
                            </button>
                          ) : null}
                        </span>
                      </div>
                      <div className="shop-list-group-items">
                        {recipeGroup.items.map((item) => (
                          <GroceryListItem
                            key={`${recipeGroup.groupKey}:${item.id}`}
                            item={item}
                            onToggleComplete={toggleItemComplete}
                            onRemove={removeItem}
                            inPantry={lineMatchesPantry(item.item)}
                            isMobile={isShopMobile}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : sortBy === 'category' ? (
                <div className="shop-list-groups">
                  {categoryGroupsView.map((categoryGroup) => (
                    <div key={categoryGroup.groupKey} className="shop-list-group">
                      <div className="shop-list-group-header">
                        <span className={`shop-list-group-label${isShopMobile ? ' shop-list-group-label--sentence' : ''}`}>
                          <i className="fas fa-tags shop-list-group-icon" aria-hidden="true" />
                          <span className="shop-list-group-label-text">{categoryGroup.category}</span>
                        </span>
                        <span className="shop-list-group-count">
                          {categoryGroup.items.length}{' '}
                          {categoryGroup.items.length === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                      <div className="shop-list-group-items">
                        {categoryGroup.items.map((item) => (
                          <GroceryListItem
                            key={item.id}
                            item={item}
                            onToggleComplete={toggleItemComplete}
                            onRemove={removeItem}
                            inPantry={lineMatchesPantry(item.item)}
                            isMobile={isShopMobile}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="shop-list-item-stack">
                  {alphaSortedItems.map((item) => (
                    <GroceryListItem
                      key={item.id}
                      item={item}
                      onToggleComplete={toggleItemComplete}
                      onRemove={removeItem}
                      inPantry={lineMatchesPantry(item.item)}
                      isMobile={isShopMobile}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="shop-list-footer-actions">
            {localItems.length > 0 ? (
              <button
                type="button"
                className="shop-reset-link"
                onClick={() => setShowClearConfirm(true)}
                aria-label="Clear grocery list"
              >
                Clear list
              </button>
            ) : null}
          </div>
        </div>

        {!isShopMobile ? (
          <ShopV2Sidebar
            checked={shopCheckedCount}
            total={shopTotalCount}
            allDone={shopAllDone}
            selectedStore={selectedStore}
            onOpenStoreLayout={selectedStore ? () => setShowStoreLayoutModal(true) : null}
            canCopyTextList={shopTotalCount > 0}
            textListCopied={textListCopied}
            onCopyTextList={copyTextList}
          />
        ) : null}
      </div>

      {/* Fixed above tab bar so list → Add scroll without chrome between them */}
      {isShopMobile
        ? createPortal(
            <ShopV2MobileBottomBar
              checked={shopCheckedCount}
              total={shopTotalCount}
              allDone={shopAllDone}
              selectedStore={selectedStore}
              onOpenStoreLayout={selectedStore ? () => setShowStoreLayoutModal(true) : null}
              canCopyTextList={shopTotalCount > 0}
              textListCopied={textListCopied}
              onCopyTextList={copyTextList}
            />,
            document.body
          )
        : null}

      {recipeRemoval && createPortal(
        <div
          className="modal-overlay modal-overlay--elevated"
          role="dialog"
          aria-modal="true"
          aria-labelledby="recipe-removal-title"
          aria-describedby="recipe-removal-description"
          onClick={(e) => {
            if (e.target === e.currentTarget) setRecipeRemoval(null)
          }}
        >
          <div
            className="card shop-confirm-panel shop-confirm-panel--wide"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="recipe-removal-title" className="shop-clear-confirm-title">
              {recipeRemoval.title}
            </h2>
            <p id="recipe-removal-description" className="shop-clear-confirm-description">
              {recipeRemoval.description}
            </p>
            <div className="shop-confirm-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setRecipeRemoval(null)}
                className="shop-clear-confirm-cancel-btn"
              >
                Not now
              </Button>
              <button
                type="button"
                onClick={confirmRecipeRemoval}
                className="shop-danger-btn"
              >
                {recipeRemoval.confirmLabel}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Clear list confirmation - portaled so always in viewport on mobile */}
      {showClearConfirm && createPortal(
        <div
          className="modal-overlay modal-overlay--elevated"
          role="dialog"
          aria-modal="true"
          aria-labelledby="clear-confirm-title"
          aria-describedby="clear-confirm-description"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowClearConfirm(false)
          }}
        >
          <div
            className="card shop-confirm-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="clear-confirm-title" className="shop-clear-confirm-title">
              Clear grocery list?
            </h2>
            <p id="clear-confirm-description" className="shop-clear-confirm-description">
              Are you sure you want to clear the list? This will remove all items (including found items) and cannot be undone.
            </p>
            <div className="shop-confirm-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowClearConfirm(false)}
                className="shop-clear-confirm-cancel-btn"
                aria-label="Cancel clearing the list"
              >
                Cancel
              </Button>
              <button
                type="button"
                onClick={clearAll}
                className="shop-danger-btn"
                aria-label="Confirm clear grocery list"
              >
                Clear list
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Store Layout Modal - portal so it overlays both Item Lookup and Item List at 90% viewport */}
      {showStoreLayoutModal && selectedStore && createPortal(
          <div
            className="modal-overlay store-layout-modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowStoreLayoutModal(false)
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="store-layout-modal-title"
          >
            <div
              className="modal-content store-layout-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="modal-header">
                <h2 id="store-layout-modal-title">{selectedStore.name} Store Layout</h2>
                <div className="shop-store-layout-actions">
                  <button
                    type="button"
                    onClick={refreshStoreLayoutMappings}
                    disabled={isRefreshingMappings || !selectedStore?.id || localItems.length === 0}
                    className="link-cta shop-store-layout-refresh-btn"
                    aria-label="Refresh aisle mappings"
                    title="Refresh aisle mappings for current list"
                  >
                    {isRefreshingMappings ? (
                      <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                    ) : (
                      <i className="fas fa-sync-alt" aria-hidden="true" />
                    )}
                  </button>
                  <button
                    onClick={() => setShowStoreLayoutModal(false)}
                    className="modal-close"
                    aria-label="Close store layout modal"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Modal Content - HTML content or message */}
              <div className="modal-body store-layout-modal-body">
                {loadingLayout ? (
                  <div className="shop-layout-loading">
                    <i className="fas fa-spinner fa-spin icon-3xl icon-primary shop-layout-loading-icon" aria-hidden="true"></i>
                    <p className="shop-store-layout-empty-text">
                      Loading store layout...
                    </p>
                  </div>
                ) : selectedStore ? (
                  <div className="shop-store-layout-schematic-wrap">
                    <StoreLayoutSchematic
                      groceryItems={localItems}
                      onItemClick={(itemId) => {
                        // Toggle item completion when clicked in schematic
                        toggleItemComplete(itemId)
                      }}
                      storeName={selectedStore.name}
                      storeLocation={selectedStore.location || selectedStore.name}
                      showHeader={false}
                    />
                  </div>
                ) : (
                  <div className="shop-store-layout-empty">
                    <i className="fas fa-map icon-3xl icon-muted shop-store-layout-empty-icon" aria-hidden="true"></i>
                    <p className="shop-store-layout-empty-text">Store layout not available</p>
                  </div>
                )}
              </div>
            </div>
          </div>,
        document.body
      )}
    </div>
  )
}

function SwipeableGroceryRemoveRow({ itemName, onRemove, children }) {
  const [offset, setOffset] = useState(0)
  const startX = useRef(0)
  const startY = useRef(0)
  const axisLock = useRef(null)
  const offsetRef = useRef(0)
  const contentRef = useRef(null)
  const openThreshold = Math.round(GROCERY_SWIPE_REMOVE_WIDTH * 0.45)
  const open = offset <= -openThreshold

  useEffect(() => {
    offsetRef.current = offset
  }, [offset])

  useEffect(() => {
    const el = contentRef.current
    if (!el) return undefined

    const onTouchStart = (e) => {
      startX.current = e.touches[0].clientX
      startY.current = e.touches[0].clientY
      axisLock.current = null
    }

    const onTouchMove = (e) => {
      const dx = e.touches[0].clientX - startX.current
      const dy = e.touches[0].clientY - startY.current
      if (axisLock.current == null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return
        axisLock.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      }
      if (axisLock.current !== 'x') return
      // Non-passive listener so horizontal swipe can win over page scroll
      if (e.cancelable) e.preventDefault()
      if (dx < 0) setOffset(Math.max(-GROCERY_SWIPE_REMOVE_WIDTH, dx))
      else setOffset(0)
    }

    const onTouchEnd = () => {
      if (offsetRef.current <= -openThreshold) setOffset(-GROCERY_SWIPE_REMOVE_WIDTH)
      else setOffset(0)
      axisLock.current = null
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchEnd)
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [openThreshold])

  return (
    <div className="grocery-swipe-row">
      <button
        type="button"
        className="grocery-swipe-row__action"
        style={{ width: GROCERY_SWIPE_REMOVE_WIDTH }}
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        title="Remove item"
        aria-label={`Remove ${itemName} from list`}
      >
        <i className="fas fa-trash" aria-hidden="true" />
      </button>
      <div
        ref={contentRef}
        className={`grocery-swipe-row__content${open ? ' grocery-swipe-row__content--open' : ''}`}
        style={{ transform: `translateX(${offset}px)` }}
      >
        {children}
      </div>
    </div>
  )
}

function GroceryListItem({
  item,
  onToggleComplete,
  onRemove,
  inPantry = false,
  showCategoryPill = false,
  isMobile = false,
}) {
  const [showSuggestionModal, setShowSuggestionModal] = useState(false)
  const [suggestionSubmitted, setSuggestionSubmitted] = useState(false)
  const [overflowOpen, setOverflowOpen] = useState(false)
  const overflowRef = useRef(null)

  function shouldShowSuggestionButton() {
    // Don't show if suggestion has already been submitted for this item
    if (suggestionSubmitted) {
      return false
    }
    // Smart triggering logic - only show for items that need better categorization
    if (!item.category || item.category.toLowerCase() === 'unknown') return true
    if (item.confidence && item.confidence < 0.5) return true
    if (item.method === 'llm_fallback' && item.confidence && item.confidence < 0.7) return true
    if (item.category && item.category.toLowerCase().includes('general')) return true
    return false
  }

  function handleSuggestionSubmitted(item, suggestedCategory) {
    console.log(`Suggestion submitted: ${item.item} -> ${suggestedCategory}`)
    // Mark that suggestion has been submitted for this item
    setSuggestionSubmitted(true)
  }

  const showSuggestionBtn = shouldShowSuggestionButton()
  const useSwipeRemove = Boolean(onRemove) && isMobile
  const showInlineRemove = Boolean(onRemove) && !isMobile
  const showInlineSuggest = showSuggestionBtn && !isMobile
  const showOverflowSuggest = showSuggestionBtn && isMobile

  useEffect(() => {
    if (!overflowOpen) return undefined
    function handlePointerDown(event) {
      if (overflowRef.current && !overflowRef.current.contains(event.target)) {
        setOverflowOpen(false)
      }
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') setOverflowOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [overflowOpen])

  const gridTemplateColumns = (() => {
    const parts = ['auto', 'minmax(150px, 1fr)']
    if (showCategoryPill) parts.push('minmax(72px, max-content)')
    if (showInlineSuggest || showOverflowSuggest) parts.push('auto')
    if (showInlineRemove) parts.push('auto')
    return parts.join(' ')
  })()

  const isFound = !!item.completed
  const displayQty = ingredientDisplayQty(item)
  const itemAriaName = displayQty ? `${displayQty} ${item.item}` : item.item

  const row = (
    <div
      className={`grocery-list-item${isFound ? ' grocery-list-item--found' : ''}${showSuggestionModal ? ' grocery-list-item--modal-open' : ''}${useSwipeRemove ? ' grocery-list-item--swipeable' : ''}`}
      style={{ '--grocery-item-cols': gridTemplateColumns }}
    >
      <input
        type="checkbox"
        checked={item.completed || false}
        onChange={() => onToggleComplete(item.id)}
        className="grocery-list-item-checkbox"
        title={item.completed ? 'Mark as not found' : 'Mark as found'}
        aria-label={item.completed ? `Mark ${itemAriaName} as not found` : `Mark ${itemAriaName} as found`}
      />

      <label
        className="grocery-list-item-label"
        onClick={() => onToggleComplete(item.id)}
      >
        {displayQty ? (
          <span className="grocery-list-item-qty">
            {displayQty}
          </span>
        ) : null}
        <span className="grocery-list-item-name">
          {item.item}
        </span>
        {inPantry && (
          <span
            className="badge-pantry grocery-list-item-pantry-badge"
            title="You have this in your pantry — you may not need to buy it"
            aria-label={`${item.item} is in your pantry`}
          >
            In pantry
          </span>
        )}
      </label>

      {showCategoryPill && item.category && (
        <span
          className="badge badge-category grocery-list-item-category"
          title={item.category}
        >
          {item.category}
        </span>
      )}

      {showInlineSuggest ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setShowSuggestionModal(true)
          }}
          className="ghost grocery-list-item-suggest-btn"
          title="Suggest better category"
          aria-label={`Suggest better category for ${item.item}`}
        >
          <i className="fas fa-lightbulb icon-sm icon-accent" aria-hidden="true"></i>
        </button>
      ) : null}

      {showOverflowSuggest ? (
        <div className="grocery-list-item-overflow" ref={overflowRef}>
          <button
            type="button"
            className={`ghost grocery-list-item-overflow-btn${overflowOpen ? ' is-open' : ''}`}
            aria-label={`More actions for ${item.item}`}
            aria-haspopup="menu"
            aria-expanded={overflowOpen}
            onClick={(e) => {
              e.stopPropagation()
              setOverflowOpen((open) => !open)
            }}
          >
            <span className="grocery-list-item-overflow-glyph" aria-hidden="true">⋯</span>
          </button>
          {overflowOpen ? (
            <div className="grocery-list-item-overflow-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="grocery-list-item-overflow-item"
                onClick={(e) => {
                  e.stopPropagation()
                  setOverflowOpen(false)
                  setShowSuggestionModal(true)
                }}
              >
                <i className="fas fa-lightbulb icon-sm icon-accent" aria-hidden="true" />
                Suggest better category
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {showInlineRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRemove(item.id)
          }}
          className="ghost grocery-list-item-remove"
          title="Remove item"
          aria-label={`Remove ${item.item} from list`}
        >
          ×
        </button>
      )}

      {/* Category Suggestion Modal - portaled to body so it overlays full viewport */}
      {createPortal(
        <CategorySuggestionModal
          isOpen={showSuggestionModal}
          onClose={() => setShowSuggestionModal(false)}
          item={item}
          onSuggestionSubmitted={handleSuggestionSubmitted}
          skipAutoCheck={true} // Skip auto-check since user manually clicked the lightbulb
        />,
        document.body
      )}
    </div>
  )

  if (useSwipeRemove) {
    return (
      <SwipeableGroceryRemoveRow
        itemName={item.item}
        onRemove={() => onRemove(item.id)}
      >
        {row}
      </SwipeableGroceryRemoveRow>
    )
  }

  return row
}

export default GroceryList